/**
 * Cliente de auditoría de correos recibidos (ingesta de pagos por email).
 *
 * El tesorero lo consulta desde el tab "Correos del banco" de Pagos para ver
 * qué llegó y, sobre todo, recuperar los que se descartaron por un formato aún
 * no soportado (ej. un template nuevo de un banco) vía `reprocesar`.
 */
import { apiFetch } from './client';

export type AccionEmailRecibido = 'pago_creado' | 'descartado_irrelevante';

export interface EmailRecibidoRead {
  id: string;
  remitente: string;
  asunto: string;
  cuerpo_raw: string;
  template_detectado: string | null;
  accion: AccionEmailRecibido;
  pago_id: string | null;
  motivo_descarte: string | null;
  recibido_at: string;
  /** Descarte definitivo: sello de revisión (null = sin revisar). */
  revisado_at: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface ListarEmailsParams {
  accion?: AccionEmailRecibido;
  limit?: number;
}

export function listarEmailsRecibidos(
  params: ListarEmailsParams = {},
): Promise<EmailRecibidoRead[]> {
  const qs = new URLSearchParams();
  if (params.accion) qs.set('accion', params.accion);
  qs.set('limit', String(params.limit ?? 50));
  return apiFetch<EmailRecibidoRead[]>(`/emails-recibidos/?${qs.toString()}`);
}

/** Re-corre el parser sobre un correo descartado; si ahora se reconoce, crea
 *  el pago y devuelve la fila actualizada (accion='pago_creado'). */
export function reprocesarEmail(id: string): Promise<EmailRecibidoRead> {
  return apiFetch<EmailRecibidoRead>(
    `/emails-recibidos/${id}/reprocesar`,
    { method: 'POST' },
  );
}

export interface EmailCuerpo {
  remitente: string;
  asunto: string;
  cuerpo_texto: string;
}

/** Cuerpo del correo decodificado (texto legible, sin adjuntos) para "Ver
 *  correo" — corroborar de qué se trata sin leer el .eml crudo. */
export function getCuerpoEmail(id: string): Promise<EmailCuerpo> {
  return apiFetch<EmailCuerpo>(`/emails-recibidos/${id}/cuerpo`);
}

/** Descarte definitivo: marca un correo descartado como revisado / sin acción
 *  para que deje de aparecer como pendiente. Idempotente. */
export function marcarRevisadoEmail(id: string): Promise<EmailRecibidoRead> {
  return apiFetch<EmailRecibidoRead>(
    `/emails-recibidos/${id}/marcar-revisado`,
    { method: 'POST' },
  );
}
